import React, { useMemo, useCallback } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useBlockLayout,
  useSortBy,
} from "react-table";
import styled from "styled-components";
import { FixedSizeList } from "react-window";
import { useWarehouseList } from '../../indexedDBUtility.js';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 16px;
`;

const TableStyles = styled.div`
  font-size: 0.7rem;
  border-spacing: 0;
  border: 1px solid black;
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;

  .thead { box-shadow: 0px 2px 10px black; }
  .tr:last-child .td { border-bottom: 0; }
  .th,
  .td {
    margin: 0;
    padding: 0.5rem;
    border-bottom: 1px solid black;
    border-right: 1px solid black;
  }
  .th:last-child,
  .td:last-child { border-right: 0; }

  .tfoot {
    box-shadow: none;
    .tr:first-child .td { border-top: 2px solid red; }
    font-weight: bolder;
    .td { color: #0010ff; }
  }
`;

const TheadStyles = styled.div`
  font-weight: 700;
  background-color: ${props => props.$bgColor || "white"};
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  width: 31%;
`;

const Search = styled.input`
  width: 100%;
`;

const scrollbarWidth = () => {
  const scrollDiv = document.createElement("div");
  scrollDiv.setAttribute(
    "style",
    "width:100px;height:100px;overflow:scroll;position:absolute;top:-9999px;"
  );
  document.body.appendChild(scrollDiv);
  const width = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return width;
};

const GlobalFilter = ({ filter, setFilter }) => (
  <SearchContainer>
    <Search
      value={filter || ""}
      onChange={(e) => setFilter(e.target.value)}
      placeholder="Search"
    />
  </SearchContainer>
);

const numericSort = (rowA, rowB, columnId) => {
  const a = parseFloat(rowA.values[columnId]) || 0;
  const b = parseFloat(rowB.values[columnId]) || 0;
  return a - b;
};

export const InventoryMovementsTable = ({
  tableData: data,
  warehouse,
  initialState,
  allWarehouseColumns,
  childWarehouseColumns,
  parentWarehouseColumns,
  goaWarehouseColumns,
}) => {
  const [,,,,,,,,, DC_list] = useWarehouseList();

  const defaultColumn = useMemo(
    () => ({
      Filter: () => null,
      width: 80,
    }),
    []
  );

  const columns = useMemo(() => {
    const src = DC_list.includes(warehouse)
      ? parentWarehouseColumns
      : warehouse === "All"
        ? allWarehouseColumns
        : warehouse === "Goa"
          ? goaWarehouseColumns
          : childWarehouseColumns;

    return src.map((column) => {
      if (column.accessor === "date") {
        return {
          ...column,
          disableSortBy: false,
          sortDescFirst: false,
          sortType: (rowA, rowB, id) => {
            return (
              (new Date(rowA.values[id]).getTime() || 0) -
              (new Date(rowB.values[id]).getTime() || 0)
            );
          },
        };
      }

      if (column.accessor === "category") {
        return {
          ...column,
          disableSortBy: false,
          sortType: (rowA, rowB, id) =>
            String(rowA.values[id]).localeCompare(String(rowB.values[id])),
        };
      }

      const numericAccessors = [
        "resultant_inventory_weight",
        "inventory_value",
        "resultant_inventory",
        "closing_inventory_value",
        "resultant_inventory_weight_closing"
      ];
      if (numericAccessors.includes(column.accessor)) {
        return {
          ...column,
          disableSortBy: false,
          sortType: numericSort,
        };
      }

      return {
        ...column,
        disableSortBy: true,
      };
    });
  }, [
    warehouse,
    parentWarehouseColumns,
    childWarehouseColumns,
    goaWarehouseColumns,
    allWarehouseColumns,
    DC_list,
  ]);

  const scrollBarSize = useMemo(() => scrollbarWidth(), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    totalColumnsWidth,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        filters: [{ id: "isActive", value: "All" }],
        sortBy: [{ id: "category", desc: false }],
        ...initialState,
      },
      autoResetFilters: true,
      autoResetHiddenColumns: true,
      autoResetPage: true,
      disableSortRemove: true,
    },
    useFilters,
    useGlobalFilter,
    useBlockLayout,
    useSortBy
  );

  const { globalFilter } = state;

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];
      prepareRow(row);
      return (
        <div {...row.getRowProps({ style })} className="tr">
          {row.cells.map((cell) => {
            if (cell.column.id !== "item_id") {
              return (
                <div {...cell.getCellProps()} className="td">
                  {cell.render("Cell")}
                </div>
              );
            }
            return (
              <div {...cell.getCellProps()} className="td">
                <a
                  href={`item-inventory-movements?warehouse=${warehouse}&item_id=${cell.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cell.render("Cell")}
                </a>
              </div>
            );
          })}
        </div>
      );
    },
    [prepareRow, rows, warehouse]
  );

  return (
    <Container>
      <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
      <TableStyles {...getTableProps()}>
        <div className="thead">
          {headerGroups.map((hg,index) => (
            <div {...hg.getHeaderGroupProps()} className="tr" key={`${index}-${hg.id}`}>
              {hg.headers.map((col) => (
                <TheadStyles
                  key={col.id}
                  $bgColor={col.bgColor}
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  className="th"
                >
                  {col.render("Header")}
                  <span>
                    {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                  </span>
                  {col.canFilter && (
                    <div onClick={e => e.stopPropagation()}>
                      {col.render("Filter")}
                    </div>
                  )}
                </TheadStyles>
              ))}
            </div>
          ))}
        </div>

        <div {...getTableBodyProps()}>
          <FixedSizeList
            height={500}
            itemCount={rows.length}
            itemSize={95}
            width={totalColumnsWidth + scrollBarSize}
          >
            {RenderRow}
          </FixedSizeList>
        </div>

        <div className="tfoot">
          {footerGroups.map((fg) => (
            <div {...fg.getFooterGroupProps()} className="tr" key={fg.id}>
              {fg.headers.map((col) => (
                <div {...col.getFooterProps()} className="td" key={col.id}>
                  {col.render("Footer")}
                </div>
              ))}
            </div>
          ))}
        </div>
      </TableStyles>
    </Container>
  );
};
